import React, { useEffect, useState } from "react";
import { Box, Chip, CircularProgress, Grid, Typography } from "@mui/material";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { Link } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { formatAmount, formatDate } from "../../utils/utils";

function StatCard({ icon, label, value, sub, color }) {
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        p: 3,
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "10px",
          bgcolor: `${color}18`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 13, color: "#6b7280", mb: 0.5 }}>{label}</Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#111827", lineHeight: 1 }}>
          {value}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.5 }}>{sub}</Typography>
        )}
      </Box>
    </Box>
  );
}

const typeChipSx = (type) => ({
  cart: { bgcolor: "#dbeafe", color: "#1d4ed8" },
  booking: { bgcolor: "#d1fae5", color: "#065f46" },
  pending: { bgcolor: "#fef3c7", color: "#92400e" },
  paid: { bgcolor: "#d1fae5", color: "#065f46" },
}[type] || { bgcolor: "#f3f4f6", color: "#374151" });

export default function DashboardOverview() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const uid = user?.uid;

  const [loading, setLoading] = useState(true);
  const [cartStats, setCartStats] = useState({ count: 0, total: 0 });
  const [bookingStats, setBookingStats] = useState({ count: 0, total: 0 });
  const [recentTx, setRecentTx] = useState([]);

  useEffect(() => {
    if (!uid) return;
    (async () => {
      try {
        const [txSnap, bookingSnap] = await Promise.all([
          getDocs(query(collection(db, "transactions"), where("userId", "==", uid))),
          getDocs(query(collection(db, "booking-list"), where("userId", "==", uid))),
        ]);

        const txDocs = txSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const bookDocs = bookingSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const paidCart = txDocs.filter((t) => t.type === "cart" && t.status === "paid");
        setCartStats({
          count: paidCart.length,
          total: paidCart.reduce((s, t) => s + (parseFloat(t.amount) || 0), 0),
        });

        const paidBookings = bookDocs.filter((b) => b.status === "paid");
        setBookingStats({
          count: paidBookings.length,
          total: paidBookings.reduce((s, b) => s + (parseFloat(b.amount) || 0), 0),
        });

        const sorted = [...txDocs].sort(
          (a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0)
        );
        setRecentTx(sorted.slice(0, 5));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [uid]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress sx={{ color: "#194049" }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
        <Typography sx={{ fontSize: 14, color: "#6b7280" }}>
          Welcome back, <strong>{user?.firstname} {user?.lastname}</strong>
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {[
            { to: "/", label: "Home", icon: <HomeOutlinedIcon sx={{ fontSize: 15 }} /> },
            { to: "/shop", label: "Shop", icon: <StorefrontOutlinedIcon sx={{ fontSize: 15 }} /> },
          ].map(({ to, label, icon }) => (
            <Box
              key={to}
              component={Link}
              to={to}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.75,
                py: 0.75,
                bgcolor: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                textDecoration: "none",
                fontSize: 13,
                color: "#374151",
                fontWeight: 500,
                transition: "border-color 0.15s, color 0.15s",
                "&:hover": { borderColor: "#194049", color: "#194049" },
              }}
            >
              {icon}
              {label}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <StatCard
            icon={<ShoppingBagIcon />}
            label="Total Purchases"
            value={formatAmount(cartStats.total)}
            sub={`${cartStats.count} order${cartStats.count !== 1 ? "s" : ""}`}
            color="#194049"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <StatCard
            icon={<CalendarMonthIcon />}
            label="Total Bookings"
            value={formatAmount(bookingStats.total)}
            sub={`${bookingStats.count} booking${bookingStats.count !== 1 ? "s" : ""}`}
            color="#7c3aed"
          />
        </Grid>
      </Grid>

      {/* Recent transactions */}
      <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
        <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #e5e7eb" }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111827" }}>
            Recent Transactions
          </Typography>
        </Box>
        {recentTx.length === 0 ? (
          <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
            <Typography sx={{ color: "#9ca3af", fontSize: 14 }}>No transactions yet</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                  {["Reference", "Amount", "Type", "Status", "Date"].map((h) => (
                    <th
                      key={h}
                      style={{ padding: "10px 16px", textAlign: "left", fontSize: 12, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentTx.map((tx) => (
                  <tr key={tx.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#374151", fontFamily: "monospace", whiteSpace: "nowrap" }}>
                      {tx.paymentIntentId || "—"}
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 13, fontWeight: 600, color: "#111827" }}>
                      {formatAmount(tx.amount)}
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <Chip
                        label={tx.type || "cart"}
                        size="small"
                        sx={{ ...typeChipSx(tx.type), fontSize: 11, height: 22, textTransform: "capitalize" }}
                      />
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <Chip
                        label={tx.status || "pending"}
                        size="small"
                        sx={{ ...typeChipSx(tx.status), fontSize: 11, height: 22, textTransform: "capitalize" }}
                      />
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#6b7280", whiteSpace: "nowrap" }}>
                      {tx.createdAt?.toDate ? formatDate(tx.createdAt.toDate()) : tx.purchase_time ? formatDate(tx.purchase_time) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Box>
    </Box>
  );
}
